import { drawing as d, geometry as g } from '@progress/kendo-drawing';
import { AutoSizeableMixin } from './AutoSizeableMixin';
import { VisualBase } from './VisualBase';
import { deepExtend } from './../utils/deep-extend';
import { defined, isNumber } from './../utils';
import { getSpacing } from './utils';
import { EMPTY, PX, SPACE } from '../constants';
const textColor = (options) => {
    if (options && options.color) {
        options = deepExtend({}, options, {
            fill: {
                color: options.color
            }
        });
    }
    return options;
};
/** @hidden */
export const defaultTextOptions = {
    fontSize: 15,
    fontFamily: 'sans-serif',
    lineSpacing: 0,
    textWrap: 'nowrap',
    padding: getSpacing(0),
    relativePadding: getSpacing(0),
    stroke: {
        width: 0
    },
    fill: {
        color: 'black'
    },
    autoSize: true
};
/**
 * A visual element that renders text content.
 * Extends VisualBase and provides functionality for creating and managing text graphics with font styling.
 */
export class TextBlock extends VisualBase {
    /**
     * Creates a new TextBlock instance.
     * @param options Configuration options for the text block
     */
    constructor(options) {
        options = deepExtend({}, defaultTextOptions, options);
        options = textColor(options);
        super(options);
        /** Array of text elements for multi-line text support. */
        this.textElements = [];
        /** Whether the text block can be aligned within its container. */
        this.alignable = true;
        /** @hidden */
        this.name = 'TextBlock';
        this._setScale = AutoSizeableMixin._setScale.bind(this);
        this._setTranslate = AutoSizeableMixin._setTranslate.bind(this);
        this._initSize = AutoSizeableMixin._initSize.bind(this);
        this._updateSize = AutoSizeableMixin._updateSize.bind(this);
        this._font();
        this._initText();
        this._initSize();
    }
    /** @hidden */
    _initText() {
        const options = this.options;
        this.textElements.length = 0;
        this.drawingElement = new d.Text(defined(options.text) ? options.text : EMPTY, new g.Point(), {
            font: options.font
        });
        this.textElements.push(this.drawingElement);
        this._fill();
        this._stroke();
    }
    /** @hidden */
    _fill(fillOptions) {
        const fill = this._evalFillOptions(fillOptions);
        if (fill.gradient) {
            const gradient = fill.gradient;
            const GradientClass = (gradient.type === 'radial' ? d.RadialGradient : d.LinearGradient);
            this.textElements.forEach((el) => {
                el.fill(new GradientClass(gradient));
            });
        }
        else {
            this._setFill(fill);
        }
    }
    /** @hidden */
    _setFill(fill) {
        this.textElements.forEach((el) => {
            el.fill(fill.color, fill.opacity);
        });
    }
    /** @hidden */
    _stroke(strokeOptions) {
        const stroke = this._evalStrokeOptions(strokeOptions);
        this.textElements.forEach(el => el.options.set('stroke', stroke));
    }
    /** @hidden */
    _font() {
        const options = this.options;
        if (options.fontFamily && defined(options.fontSize)) {
            const fontOptions = [];
            if (options.fontStyle) {
                fontOptions.push(options.fontStyle);
            }
            if (options.fontWeight) {
                fontOptions.push(options.fontWeight);
            }
            fontOptions.push(options.fontSize + (isNumber(options.fontSize) ? PX : EMPTY));
            fontOptions.push(options.fontFamily);
            options.font = fontOptions.join(SPACE);
        }
        else {
            delete options.font;
        }
    }
    /**
     * Gets or sets the text content of the text block.
     * @param text The text content to set, or undefined to get current content
     * @returns The current text content when called as getter
     */
    content(text) {
        return this.drawingElement.content(text);
    }
    /**
     * Redraws the text block with updated options.
     * @param options New options to apply to the text block
     */
    redraw(options) {
        if (options) {
            let sizeChanged = false;
            const textOptions = this.options;
            options = textColor(options);
            super.redraw(options);
            if (options.fontFamily || defined(options.fontSize) || options.fontStyle || options.fontWeight) {
                deepExtend(textOptions, {
                    fontFamily: options.fontFamily,
                    fontSize: options.fontSize,
                    fontStyle: options.fontStyle,
                    fontWeight: options.fontWeight
                });
                this._font();
                this.drawingElement.options.set('font', textOptions.font);
                sizeChanged = true;
            }
            if (options.text) {
                this.content(options.text);
                sizeChanged = true;
            }
            if (!this._updateSize(options) && sizeChanged) {
                this._initSize();
            }
        }
    }
}
